import type {Component, Editor} from 'grapesjs';
import {POSTERMYWALL_FORM_BACKGROUND_TYPE} from '@FormMakerReact/components/postermywall-form-background/postermywall-form-background';
import {FormMakerFontFamily} from '@FormMakerReact/lib/form-maker-typography';
import {parsePrimaryFontFamily} from '@Utils/string.util';

const SUBMIT_BUTTON_CONTAINER_CLASS = 'postermywall-submit-button-container';

const SUBMIT_BUTTON_CONTAINER_DEFAULT_BG = '#fff';

export function getComponentStyleProperty(component: Component, property: string): string {
  const style = component.getStyle() as Record<string, string | undefined>;
  return style[property] ?? '';
}

export function setComponentStyleProperty(component: Component, property: string, value: string): void {
  if (getComponentStyleProperty(component, property) === value) {
    return;
  }
  component.addStyle({[property]: value});
}

export function clearComponentStyleProperty(component: Component, property: string): void {
  component.removeStyle(property);
}

export interface FontFamilyOption {
  readonly value: string;
  readonly name: string;
}

export const FORM_MAKER_FONT_OPTIONS: readonly FontFamilyOption[] = [
  {value: FormMakerFontFamily.ARIAL, name: 'Arial'},
  {value: FormMakerFontFamily.ARIAL_BLACK, name: 'Arial Black'},
  {value: FormMakerFontFamily.BRUSH_SCRIPT_MT, name: 'Brush Script MT'},
  {value: FormMakerFontFamily.COMIC_SANS_MS, name: 'Comic Sans MS'},
  {value: FormMakerFontFamily.COURIER_NEW, name: 'Courier New Courier'},
  {value: FormMakerFontFamily.GEORGIA, name: 'Georgia'},
  {value: FormMakerFontFamily.HELVETICA, name: 'Helvetica'},
  {value: FormMakerFontFamily.IMPACT, name: 'Impact'},
  {value: FormMakerFontFamily.LUCIDA_SANS_UNICODE, name: 'Lucida Sans Unicode'},
  {value: FormMakerFontFamily.TAHOMA, name: 'Tahoma'},
  {value: FormMakerFontFamily.TIMES_NEW_ROMAN, name: 'Times New Roman'},
  {value: FormMakerFontFamily.TREBUCHET_MS, name: 'Trebuchet MS'},
  {value: FormMakerFontFamily.VERDANA, name: 'Verdana'},
];

export function findFontOption(fullFamily: string): FontFamilyOption | undefined {
  if (!fullFamily) {
    return undefined;
  }
  const exact = FORM_MAKER_FONT_OPTIONS.find((opt) => opt.value === fullFamily);
  if (exact) {
    return exact;
  }
  const primary = parsePrimaryFontFamily(fullFamily);
  return FORM_MAKER_FONT_OPTIONS.find((opt) => parsePrimaryFontFamily(opt.value) === primary);
}

export function setComponentFontFamily(component: Component, fontFamily: string): void {
  setComponentStyleProperty(component, 'font-family', fontFamily);
  if (component.get('type') !== POSTERMYWALL_FORM_BACKGROUND_TYPE) {
    return;
  }
  forEachDescendantWithFontFamily(component, (descendant): void => {
    setComponentStyleProperty(descendant, 'font-family', fontFamily);
  });
}

function forEachDescendantWithFontFamily(component: Component, visit: (c: Component) => void): void {
  const children = component.components();
  if (!children) {
    return;
  }
  children.forEach((child: Component): void => {
    const childStyle = child.getStyle() as Record<string, string | undefined>;
    if (childStyle['font-family'] !== undefined) {
      visit(child);
    }
    forEachDescendantWithFontFamily(child, visit);
  });
}

export function syncSubmitButtonContainerBackground(formBackground: Component): void {
  if (formBackground.get('type') !== POSTERMYWALL_FORM_BACKGROUND_TYPE) {
    return;
  }
  const formBgColor = getComponentStyleProperty(formBackground, 'background-color');
  const targetColor = formBgColor === '' ? SUBMIT_BUTTON_CONTAINER_DEFAULT_BG : formBgColor;
  const containers = formBackground.find(`.${SUBMIT_BUTTON_CONTAINER_CLASS}`);
  containers.forEach((container: Component): void => {
    setComponentStyleProperty(container, 'background-color', targetColor);
  });
}

export function syncAllSubmitButtonContainerBackgrounds(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const formBackgrounds = wrapper.findType(POSTERMYWALL_FORM_BACKGROUND_TYPE);
  formBackgrounds.forEach((formBackground: Component): void => {
    syncSubmitButtonContainerBackground(formBackground);
  });
}
